/**
 * DeactivateAutomaticTeamModal
 *
 * A multi-step modal for deactivating an automatic team and handling reassignment
 * of team members to other managers.
 */
import SimpleModal from "app/components/Modals/SimpleModal";
import Button from "app/storybookComponents/Button";
import { Separator } from "app/shadcnComponents/ui/separator";
import { useAppDispatch, useAppSelector } from "utils/redux/hooks";
import {
  selectAllCompanyUsersById,
  selectTeamsByTeamId,
} from "app/containers/Global/slice";
import {
  deactivateAutomaticTeam,
  selectDeactivateAutomaticTeamStatus,
} from "../slice";
import { Team } from "app/containers/Global/types";
import { useMemo, useState } from "react";
import { getS } from "utils/helperFunctions";
import { toast } from "react-toastify";
import { successNotificationOptions } from "utils/constants";
import UserSelector from "app/components/DeactivateUserModal/UserSelector";
import { selectManagerAndDirectReportsByUserAccountId } from "app/containers/AdvancedCsvUpload/slice";

/**
 * Component props interface
 */
interface Props {
  isOpen: boolean; // Controls modal visibility
  onHide: () => void; // Function to close the modal
  teamId?: number | null; // ID of the team to be deactivated
}

/**
 * Modal component for deactivating automatic teams with a multi-step process
 */
const DeactivateAutomaticTeamModal = ({ isOpen, onHide, teamId }: Props) => {
  // Redux hooks
  const dispatch = useAppDispatch();
  const teamsByTeamId = useAppSelector(selectTeamsByTeamId);
  const usersInfoById = useAppSelector(selectAllCompanyUsersById);
  const deactivateAutomaticTeamStatus = useAppSelector(
    selectDeactivateAutomaticTeamStatus
  );
  const managersAndDirectReportsByUserAccountId = useAppSelector(
    selectManagerAndDirectReportsByUserAccountId
  );

  // Team data
  const teamInfo = teamId && teamsByTeamId[teamId];

  // Modal state management
  const [step, setStep] = useState(1); // Current step in the wizard (1-3)
  const [selectedReassignmentOption, setSelectedReassignmentOption] = useState<
    "all" | "some"
  >("all"); // Whether to reassign all members to one manager or handle individually
  const [newManagerId, setNewManagerId] = useState<number | null>(null); // ID of the selected new manager

  /**
   * Get the full name of the team leader
   */
  const teamLeaderName = useMemo(() => {
    if (!teamInfo) return "";
    const teamLeader = teamInfo.teamLeadUserAccountIds?.[0];
    const teamLeaderInfo = teamLeader ? usersInfoById[teamLeader] : null;
    if (!teamLeaderInfo) return "User";
    return teamLeaderInfo.firstName + " " + teamLeaderInfo.lastName;
  }, [teamInfo, usersInfoById]);

  /**
   * Handle the final submission based on selected option
   * Executes the appropriate API call based on user's selection
   */
  const handleFinalSubmission = async () => {
    try {
      await dispatch(
        deactivateAutomaticTeam({
          teamId: teamId!,
          newManagerUserAccountId: newManagerId,
        })
      ).unwrap();
      handleCloseModal();
      toast.success("Automatic team deactivated successfully!", {
        ...successNotificationOptions,
      });
    } catch (_) {
      toast.error("Looks like something went wrong. Try again later.", {
        ...successNotificationOptions,
        className: "error",
      });
    }
  };

  /**
   * Handle continue button click
   * Either moves to the next step or submits the final action
   */
  const handleContinueClick = () => {
    if (step >= 3) {
      return handleFinalSubmission();
    }
    setStep(step + 1);
  };

  /**
   * Handle previous button click
   * Either moves to the previous step or closes the modal
   */
  const handlePreviousStep = () => {
    if (step <= 1) {
      return handleCloseModal();
    }
    setStep(step - 1);
  };

  /**
   * Renders the manager selection dropdown
   * @param team - The team object containing member information
   */
  const renderManagerSelectionDropdown = (team: Team) => {
    const excludedUserAccountIds: number[] = [];

    if (team?.teamMemberIds) {
      excludedUserAccountIds.push(...team.teamMemberIds);
    }

    return (
      <>
        <p className="text-sm text-gray-600">Select a leader/manager</p>
        <UserSelector
          onUserSelected={(userAccountId) => {
            setNewManagerId(Number(userAccountId) || null);
          }}
          placeholderText="Select a new manager"
          excludeUserAccountIds={excludedUserAccountIds}
        />
      </>
    );
  };

  /**
   * Renders the selected manager card with information and remove option
   */
  const renderSelectedManagerCard = () => {
    if (!newManagerId || !usersInfoById[newManagerId]) return null;
    const selectedManager = usersInfoById[newManagerId];

    const isNewManager =
      !managersAndDirectReportsByUserAccountId[newManagerId]
        ?.directReportRelationships?.length;

    return (
      <div className="flex items-center justify-between p-4 bg-gray-50 border border-gray-200 rounded-md">
        <div className="flex items-center gap-2">
          <div>
            <p className="font-medium">
              <b>
                {selectedManager.firstName} {selectedManager.lastName}
              </b>
            </p>
            <p className="text-sm text-gray-600">
              {selectedManager.jobTitle ?? "No Job Title"}
            </p>
          </div>
          {isNewManager && <span className="label-tag green">New manager</span>}
        </div>
        <div className="flex gap-3">
          <Button
            variant="secondary-gray"
            size="sm"
            className="text-sm py-1 px-3"
            onClick={() => setNewManagerId(null)}
          >
            Remove
          </Button>
        </div>
      </div>
    );
  };

  /**
   * Renders the content for step 1: Confirmation
   * Shows warning and explanation about team deactivation
   * @param team - The team to be deactivated
   */
  const renderStep1Content = () => {
    return (
      <div className="space-y-4">
        <h3>
          Are you sure you want to delete {teamLeaderName}'s Automatic team?
        </h3>
        <p className="text-gray-700">
          This will permanently delete all team information/assessments, team
          guide details, and all team members from the team. This action cannot
          be undone. To reassign individuals to a team, assign a manager within
          the People table, or reupload a CSV with the correct manager -
          employee relationships.
        </p>
        <div className="bg-amber-50 border border-amber-200 p-4 rounded-lg mt-4">
          <p className="text-amber-800">
            To deactivate an automatic team, you have to reassign all direct
            reports to a new manager or deactivate the manager in the system.
          </p>
        </div>
      </div>
    );
  };

  /**
   * Renders a radio button option for step 2
   * @param isSelected - Whether this option is currently selected
   * @param onSelect - Function to call when this option is selected
   * @param title - Title text for the option
   * @param description - Description text for the option
   */
  const renderOptionSelector = (
    isSelected: boolean,
    onSelect: () => void,
    title: string,
    description: string
  ) => {
    return (
      <button
        onClick={onSelect}
        className={`w-full flex items-start gap-3 p-4 border rounded-lg transition-colors ${
          isSelected
            ? "bg-blue-50 border-blue-600"
            : "bg-white border-gray-200 hover:bg-gray-50"
        }`}
      >
        <div className="mt-1">
          <div
            className={`h-5 w-5 rounded-full flex items-center justify-center ${
              isSelected ? "bg-blue-600" : "border border-gray-300"
            }`}
          >
            {isSelected && (
              <svg
                className="h-3 w-3 text-white"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                  clipRule="evenodd"
                />
              </svg>
            )}
          </div>
        </div>
        <div className="flex flex-col text-left">
          <p className="font-medium text-gray-900">{title}</p>
          <p className="text-sm text-gray-500 mt-1">{description}</p>
        </div>
      </button>
    );
  };

  /**
   * Renders the content for step 2: Reassignment options
   * Shows selection between reassigning all members or handling individually
   * @param team - The team to be deactivated
   */
  const renderStep2Content = (team: Team) => {
    const totalTeamMembers = (team.teamMemberIds?.length ?? 1) - 1;
    return (
      <div className="space-y-4">
        <h3>
          This team has {totalTeamMembers} members. How would you like to
          reassign them?
        </h3>
        <div className="flex gap-2">
          {renderOptionSelector(
            selectedReassignmentOption === "all",
            () => setSelectedReassignmentOption("all"),
            "Reassign all direct reports to 1 new manager",
            "You will need to reassign each direct report to a single manager"
          )}
          {renderOptionSelector(
            selectedReassignmentOption === "some",
            () => setSelectedReassignmentOption("some"),
            "Reassign some (not all) direct reports to different manager(s)",
            "You will have to reassign direct reports to a new manager in the “People” table."
          )}
        </div>
      </div>
    );
  };

  /**
   * Renders the content for step 3: Manager selection or confirmation
   * Shows either manager selection dropdown or confirmation of individual reassignment
   * @param team - The team to be deactivated
   */
  const renderStep3Content = (team: Team) => {
    if (selectedReassignmentOption === "some") {
      return (
        <div>
          <p>
            Team gets deactivated. All people in team will not have direct
            manager.
          </p>
          <p>
            Go to people table and reassign each member to the appropriate
            manager.
          </p>
        </div>
      );
    }
    const totalTeamMembers = (team.teamMemberIds?.length ?? 1) - 1;

    return (
      <div className="flex flex-col gap-2">
        <p>
          <b>{teamLeaderName}'s</b> is the current leader of the team and will
          remain in the system.
        </p>
        <p className="mb-2">
          Reassign {totalTeamMembers !== 1 ? "all " : ""}
          <b>
            {totalTeamMembers} member{getS(totalTeamMembers)}
          </b>{" "}
          of the team from {teamLeaderName} to the appropriate manager.
        </p>
        {renderManagerSelectionDropdown(team)}
        {renderSelectedManagerCard()}
      </div>
    );
  };

  /**
   * Renders the appropriate content based on current step
   */
  const renderModalContent = () => {
    if (!teamInfo) return null;
    switch (step) {
      case 1:
        return renderStep1Content();
      case 2:
        return renderStep2Content(teamInfo);
      case 3:
        return renderStep3Content(teamInfo);
      default:
        return null;
    }
  };

  /**
   * Gets the subtitle text for the current step
   */
  const getStepSubtitle = () => {
    if (step === 1) {
      return "Confirmation";
    }
    if (step === 2) {
      return "Reassign direct reports";
    }
    if (step === 3) {
      return selectedReassignmentOption === "all"
        ? "Reassign reports to a new leader"
        : "Unassign reports from manager";
    }
    return "";
  };

  /**
   * Determines if the continue button should be disabled
   * (Currently only disabled when a new manager is required but not selected)
   */
  const isContinueButtonDisabled = () => {
    if (step === 3 && selectedReassignmentOption === "all" && !newManagerId) {
      return true;
    }
    if (deactivateAutomaticTeamStatus === "loading") {
      return true;
    }
    return false;
  };

  /**
   * Handles modal closing and resets all state
   */
  const handleCloseModal = () => {
    onHide();
    setStep(1);
    setSelectedReassignmentOption("all");
    setNewManagerId(null);
  };

  return (
    <SimpleModal
      show={isOpen}
      onHide={handleCloseModal}
      title={`Deactivate ${teamLeaderName}'s Automatic Team`}
      additionalCloseButtonContent={
        <div className="inline-block text-sm">Step {step} of 3</div>
      }
    >
      <div className="text-sm mt-[-20px]">
        <p>{getStepSubtitle()}</p>
      </div>
      <Separator />
      <div>{renderModalContent()}</div>
      <Separator />
      <div className="gap-2 flex justify-end">
        <Button
          onClick={handlePreviousStep}
          variant="secondary-blue"
          className="mr-2"
        >
          Cancel
        </Button>
        <Button
          onClick={handleContinueClick}
          disabled={isContinueButtonDisabled()}
        >
          Continue
        </Button>
      </div>
    </SimpleModal>
  );
};

export default DeactivateAutomaticTeamModal;
